import StatusSwitch from 'components/StatusSwitch';
import { useTranslate } from '@tolgee/react';
import { updatePauseTask } from '../service';
import { Switch, Tooltip, message } from 'antd';
import { useMemo } from 'react';
import { Container } from './PauseMode';
import backgroundImage from 'views/Knowledge/assets/titleBackground.png';
import styled from '@emotion/styled';
import { useNewQAEnable } from 'hooks/useNewQAEnable';

export interface PauseScopeProps {
  readonly?: boolean;
  value: Aide.Pause.Task;
  onChange: (value: Partial<Aide.Pause.Task>) => void;
}

function PauseScope(props: PauseScopeProps) {
  const { readonly = false, value, onChange } = props;

  const {
    permScope: { permScopeList },
  } = value;

  const { t } = useTranslate();

  const newQAEnable = useNewQAEnable();

  const { scopeLabelMap, scopeScenariosLabelMap } = useMemo(() => {
    const scopeLabelMap: Record<Aide.Pause.ScopeName, string> = {
      RECEPTION_BASICS: t('aide.基础接待'),
      AI_RECEPTIONIST: t('domain.aiReception.type.aiReception'),
      ORDER_REMINDER: t('marketing.催下单'),
      PAYMENT_REMINDER: t('marketing.催付款'),
      LOGISTICS_CARE: t('domain.aiReception.type.logisticsCare'),
      CANCELLATION_REFUND_FOLLOW_UP: t('aide.弃单挽回'),
      REVIEW_BOOSTER: t('aide.评价管理'),
    };

    const scopeScenariosLabelMap: Record<Aide.Pause.ScopeName, string[]> = {
      RECEPTION_BASICS: [
        t('domain.aiReception.type.storeWelcomeMessage'),
        t('nav.autoReply'),
        t('aide.接待评分'),
      ],
      AI_RECEPTIONIST: [
        t('aide.官方场景'),
        t('aide.店铺场景'),
        t('aide.值守兜底回复'),
        t('advertising_promotion_automatic_reply'),
      ],
      ORDER_REMINDER: [t('marketing.催下单')],
      PAYMENT_REMINDER: [t('marketing.催付款')],
      LOGISTICS_CARE: [
        t('domain.aiReception.type.orderConfirmed'),
        t('domain.aiReception.type.delayedShipment'),
        t('domain.aiReception.type.shipped'),
        t('domain.aiReception.type.lastMileDelivery'),
        t('domain.aiReception.type.waitingForRedelivery'),
        t('domain.aiReception.type.received'),
      ],
      CANCELLATION_REFUND_FOLLOW_UP: [
        t('domain.aiReception.type.refundOnlyOrReturnRefund'),
        t('aide.买家取消订单'),
        t('aide.系统取消订单'),
      ],
      REVIEW_BOOSTER: [
        t('marketing.催好评'),
        t('domain.aiReception.type.positiveReviewFollowUp'),
        t('domain.aiReception.type.negativeReviewRecovery'),
      ],
    };

    return { scopeLabelMap, scopeScenariosLabelMap };
  }, [t]);

  return (
    <Container>
      <header>
        <h3>{t('auto_reply_pause_scope')}</h3>
      </header>
      <Content>
        {Object.keys(scopeLabelMap).map((scope) => {
          const permScope = permScopeList.find((_) => _.name === scope);

          if (!permScope) {
            return null;
          }

          const { name, isSelect } = permScope;
          const { [name]: scopeLabel } = scopeLabelMap;
          const { [name]: scopeScenariosLabel } = scopeScenariosLabelMap;

          const switchNode =
            name === 'AI_RECEPTIONIST' && !newQAEnable ? (
              <Tooltip title={t('aide.当前服务套餐，暂无AI智能接待权限')}>
                <Switch
                  disabled
                  checkedChildren={t('marketing.启用中')}
                  unCheckedChildren={t('marketing.未启用')}
                  checked={isSelect}
                />
              </Tooltip>
            ) : (
              <StatusSwitch
                disabled={readonly}
                checkedChildren={t('marketing.启用中')}
                unCheckedChildren={t('marketing.未启用')}
                checked={isSelect}
                onChange={(isSelect) => {
                  const permScope = {
                    permScopeList: permScopeList.map((_) => {
                      if (_.name !== name) {
                        return _;
                      }

                      return {
                        name,
                        isSelect,
                      };
                    }),
                  };

                  return updatePauseTask({
                    ...value,
                    permScope,
                  })
                    .then(() => {
                      onChange({ permScope });

                      message.success(t('operation.saveSucceed'));
                    })
                    .catch(() => {
                      message.error(t('operation.saveFailed'));
                    });
                }}
              />
            );

          return (
            <dl key={name}>
              <dt>
                {scopeLabel}
                {switchNode}
              </dt>
              <dd>
                {t('reply_content_of_x', {
                  x: scopeScenariosLabel.join('&'),
                })}
              </dd>
            </dl>
          );
        })}
      </Content>
    </Container>
  );
}

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  > dl {
    min-height: 141px;
    padding: 16px;
    margin-bottom: 0;
    background: top left / contain no-repeat url(${`"${backgroundImage}"`}),
      linear-gradient(180deg, #e0e9ff, #ffffff);
    border: solid 1px var(--fett-border-default);
    border-radius: 4px;
    > dt {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      > * {
        margin-left: 8px;
      }
    }
    > dd {
      margin-bottom: 0;
    }
  }
`;

export default PauseScope;
